declare global {
  interface Window {
    __COMPLIANCE_API_BASE_URL__?: string;
    __COMPLIANCE_ANALYTICS_PROJECT__?: string;
  }
}

type AnalyticsEventType = "pageview" | "click" | "conversion";

/** 前端埋点事件，projectKey 用于未来在同一后台切换不同项目。 */
export type AnalyticsEvent = {
  projectKey?: string;
  eventType: AnalyticsEventType;
  eventName: string;
  path?: string;
  targetLabel?: string;
  country?: string;
  locale?: string;
  organizationId?: string;
  metadata?: Record<string, unknown>;
};

const SESSION_KEY = "compliance.analytics.session";
let activeCountry = "SG";
let activeLocale = "en";
let activeOrganizationId = "";

const resolveBaseURL = (): string => window.__COMPLIANCE_API_BASE_URL__ ?? "/api";

const getSessionId = (): string => {
  const existing = localStorage.getItem(SESSION_KEY);
  if (existing) {
    return existing;
  }
  const created = crypto.randomUUID?.() ?? `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(SESSION_KEY, created);
  return created;
};

const currentPath = (path?: string): string => path ?? `${window.location.pathname}${window.location.hash || window.location.search}`;

/** 更新埋点上下文，避免每次上报都从组件里重复传国家、语言和组织。 */
export const setAnalyticsContext = (context: { country?: string; locale?: string; organizationId?: string }): void => {
  if (context.country) {
    activeCountry = context.country;
  }
  if (context.locale) {
    activeLocale = context.locale;
  }
  if (context.organizationId !== undefined) {
    activeOrganizationId = context.organizationId;
  }
};

/** 发送单条统计事件；失败时静默，避免影响主业务流程。 */
export const trackEvent = (event: AnalyticsEvent): void => {
  const payload = JSON.stringify({
    projectKey: event.projectKey ?? window.__COMPLIANCE_ANALYTICS_PROJECT__ ?? "compliance",
    eventType: event.eventType,
    eventName: event.eventName,
    path: currentPath(event.path),
    pageTitle: document.title,
    targetLabel: event.targetLabel,
    referrer: document.referrer,
    sessionId: getSessionId(),
    organizationId: event.organizationId ?? activeOrganizationId,
    country: event.country ?? activeCountry,
    locale: event.locale ?? activeLocale,
    metadata: event.metadata
  });
  const url = `${resolveBaseURL()}/analytics/events`;
  if (navigator.sendBeacon?.(url, new Blob([payload], { type: "application/json" }))) {
    return;
  }
  void fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: payload,
    keepalive: true
  }).catch(() => undefined);
};

/** 记录页面访问，用于后台 PV 汇总。 */
export const trackPageView = (page: string): void => {
  trackEvent({ eventType: "pageview", eventName: page, path: `/app/${page}` });
};

/** 记录业务转化事件，例如注册、登录、检查、抽取、生成预览和支付。 */
export const trackConversion = (eventName: string, metadata?: Record<string, unknown>): void => {
  trackEvent({ eventType: "conversion", eventName, metadata });
};

/** 安装全局点击监听，自动覆盖按钮和链接点击。 */
export const installClickTracking = (): void => {
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target instanceof Element ? event.target.closest("button,a") : null;
      if (!target) {
        return;
      }
      const label = (target.textContent ?? target.getAttribute("aria-label") ?? target.getAttribute("title") ?? "").trim().replace(/\s+/g, " ");
      const role = target.tagName.toLowerCase();
      trackEvent({
        eventType: "click",
        eventName: target.getAttribute("data-analytics") ?? `${role}_click`,
        targetLabel: label.slice(0, 120),
        metadata: {
          role,
          href: target instanceof HTMLAnchorElement ? target.href : undefined,
          className: target.getAttribute("class") ?? undefined
        }
      });
    },
    { capture: true }
  );
};
